import React, { memo } from "react";
import { PdfDocumentAction } from "app/data/shipping-docs/pdfDocumentActionStorage";
import XGSIcon from "ui-components/icon/xgsIcon";
import XGSIcons from "ui-components/icon/xgsIcons";
import Loading from "ui-components/loading/loading";
import PdfActionSplitButton from "ui-components/pdf-action-split-button/pdfActionSplitButton";
import { DocDownloadStatus, DocTypeDownloadStatusMap } from "./types";

export interface DownloadActionBarProps {
  driverReceiptCount: number;
  shippingLabelCount: number;
  deliveryManifestCount: number;
  downloadStatus: DocTypeDownloadStatusMap;
  onClearSelection: () => void;
  onDeliver: (action: PdfDocumentAction) => void;
}

const statusLabel = (status: DocDownloadStatus): string => {
  switch (status) {
    case "queued":
      return "Queued";
    case "downloading":
      return "In progress…";
    case "success":
      return "Completed";
    case "error":
      return "Failed";
    default:
      return "";
  }
};

const DownloadActionBar: React.FC<DownloadActionBarProps> = memo((props) => {
  const {
    driverReceiptCount,
    shippingLabelCount,
    deliveryManifestCount,
    downloadStatus,
    onClearSelection,
    onDeliver,
  } = props;

  const totalDocs = driverReceiptCount + shippingLabelCount + deliveryManifestCount;
  const isDownloading =
    downloadStatus.shippingLabels === "queued" ||
    downloadStatus.shippingLabels === "downloading" ||
    downloadStatus.driverReceipts === "queued" ||
    downloadStatus.driverReceipts === "downloading" ||
    downloadStatus.deliveryManifests === "queued" ||
    downloadStatus.deliveryManifests === "downloading";

  const showProgress =
    downloadStatus.shippingLabels !== "idle" ||
    downloadStatus.driverReceipts !== "idle" ||
    downloadStatus.deliveryManifests !== "idle";

  if (totalDocs === 0 && !showProgress) return null;

  const renderStatusRow = (
    key: keyof DocTypeDownloadStatusMap,
    label: string,
    count: number
  ) => {
    const status = downloadStatus[key];
    if (status === "idle") return null;

    return (
      <div
        key={key}
        className={`xgs-print-download__action-bar__progress-row xgs-print-download__action-bar__progress-row--${status}`}
      >
        <span className="xgs-print-download__action-bar__progress-icon">
          {status === "downloading" || status === "queued" ? (
            <Loading isLoading={true} />
          ) : status === "success" ? (
            <XGSIcon icon={XGSIcons.faCheckCircle} />
          ) : status === "error" ? (
            <XGSIcon icon={XGSIcons.faExclamationCircle} />
          ) : null}
        </span>
        <span>
          {label}
          {count > 0 ? ` (${count})` : ""} — {statusLabel(status)}
        </span>
      </div>
    );
  };

  return (
    <div className="xgs-print-download__action-bar">
      <div className="xgs-print-download__action-bar__main">
        <div className="xgs-print-download__action-bar__counts">
          {driverReceiptCount > 0 && (
            <span className="xgs-print-download__action-bar__chip">
              <XGSIcon icon={XGSIcons.faCheck} /> {driverReceiptCount}x Driver Receipt
              {driverReceiptCount === 1 ? "" : "s"}
            </span>
          )}
          {shippingLabelCount > 0 && (
            <span className="xgs-print-download__action-bar__chip">
              <XGSIcon icon={XGSIcons.faCheck} /> {shippingLabelCount}x Shipment Label
              {shippingLabelCount === 1 ? "" : "s"}
            </span>
          )}
          {deliveryManifestCount > 0 && (
            <span className="xgs-print-download__action-bar__chip">
              <XGSIcon icon={XGSIcons.faCheck} /> {deliveryManifestCount}x Delivery Manifest
              {deliveryManifestCount === 1 ? "" : "s"}
            </span>
          )}
        </div>
        <div className="xgs-print-download__action-bar__actions">
          <button
            type="button"
            className="xgs-print-download__action-bar__clear"
            onClick={onClearSelection}
            disabled={isDownloading}
          >
            Clear selection
          </button>
          <PdfActionSplitButton
            className="xgs-print-download__action-bar__download"
            onAction={onDeliver}
            disabled={isDownloading || totalDocs === 0}
            spinner={isDownloading}
          >
            {totalDocs} document{totalDocs === 1 ? "" : "s"}
          </PdfActionSplitButton>
        </div>
      </div>
      {showProgress && (
        <div className="xgs-print-download__action-bar__progress">
          {renderStatusRow("shippingLabels", "Shipment Labels", shippingLabelCount)}
          {renderStatusRow("driverReceipts", "Driver Receipts", driverReceiptCount)}
          {renderStatusRow("deliveryManifests", "Delivery Manifests", deliveryManifestCount)}
        </div>
      )}
    </div>
  );
});

export default DownloadActionBar;
